import { Component, OnInit, inject } from '@angular/core';
import { Button } from 'primeng/button';
import { DatePipe, NgIf } from '@angular/common';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TabMenuModule } from 'primeng/tabmenu';
import { MenuItem, PrimeTemplate, ConfirmationService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { OnBoardingRecord, WizardStatus } from '@voice/utils';
import { Router } from '@angular/router';
import { ClientOnboardingService } from '../../../services/client-onboarding.service';
import { NotificationsService } from '../../../services/notifications.service';
import { MiniStepsComponent } from './mini-steps.component';
import { PermissionsService, PermissionCheck } from '../../../services/permissions.service';

@Component({
  selector: 'app-records-list',
  standalone: true,
  imports: [
    Button,
    DatePipe,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    PrimeTemplate,
    TableModule,
    TabMenuModule,
    NgIf,
    MiniStepsComponent,
    ConfirmDialogModule,
    TooltipModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './records-list.component.html',
})
export class RecordsListComponent implements OnInit {
  private readonly permissionsService = inject(PermissionsService);

  permissions: PermissionCheck = {
    canAdd: false,
    canEdit: false,
    canDelete: false,
    canView: false,
  };

  get canAdd(): boolean {
    return this.permissions.canAdd;
  }

  get canEdit(): boolean {
    return this.permissions.canEdit;
  }

  get canDelete(): boolean {
    return this.permissions.canDelete;
  }

  records: OnBoardingRecord[] = [];
  inProgressCount = 0;
  errorCount = 0;
  finishedCount = 0;

  status = WizardStatus.IN_PROGRESS;

  showAddButton = true;

  showRemediationButton = true;

  allowEdition = true;

  items: MenuItem[] = [];
  activeItem?: MenuItem;

  constructor(
    public readonly router: Router,
    private readonly clientOnboardingService: ClientOnboardingService,
    private readonly notificationService: NotificationsService,
    private readonly confirmationService: ConfirmationService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadPermissions();
    this.loadCounts();
    this.loadData();
    this.updateMenuItems();
  }

  private async loadPermissions(): Promise<void> {
    this.permissions = await this.permissionsService.checkEntityPermissions('billing');
  }

  private loadCounts(): void {
    this.clientOnboardingService.fetch(WizardStatus.IN_PROGRESS).subscribe({
      next: (records) => {
        this.inProgressCount = records.length;
        this.updateMenuItems();
      },
      error: (err) => console.error('Error loading in progress count:', err),
    });
    this.clientOnboardingService.fetch(WizardStatus.ERROR).subscribe({
      next: (records) => {
        this.errorCount = records.length;
        this.updateMenuItems();
      },
      error: (err) => console.error('Error loading error count:', err),
    });
    this.clientOnboardingService.fetch(WizardStatus.FINISHED).subscribe({
      next: (records) => {
        this.finishedCount = records.length;
        this.updateMenuItems();
      },
      error: (err) => console.error('Error loading finished count:', err),
    });
  }

  private updateMenuItems(): void {
    this.items = [
      {
        label: `In Progress (${this.inProgressCount})`,
        icon: 'pi pi-hammer',
        command: () => {
          this.status = WizardStatus.IN_PROGRESS;
          this.activeItem = this.items[0];
          this.loadData();
        },
      },
      {
        label: `Error (${this.errorCount})`,
        icon: 'pi pi-exclamation-circle',
        command: () => {
          this.status = WizardStatus.ERROR;
          this.activeItem = this.items[1];
          this.loadData();
        },
      },
      {
        label: `Finished (${this.finishedCount})`,
        icon: 'pi pi-check',
        command: () => {
          this.status = WizardStatus.FINISHED;
          this.activeItem = this.items[2];
          this.loadData();
        },
      },
    ];
    if (!this.activeItem) {
      this.activeItem = this.items[0];
    }
  }

  onGlobalFilter(event: Event) {
    console.log(event);
  }

  loadData() {
    this.clientOnboardingService.fetch(this.status).subscribe({
      next: (records) => {
        this.records = records;
        this.loadCounts();
      },
      error: (err) => this.notificationService.error(err),
    });
  }

  edit(id: number): void {
    if (!this.canEdit) return;
    this.router.navigate(['billing/client-onboarding/wizard', id]).then();
  }

  delete(id: number): void {
    if (!this.canDelete) return;
    const record = this.records.find((r) => r.id === id);
    const recordName = record?.customerName || 'this record';

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${recordName}? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.clientOnboardingService.delete(id).subscribe({
          next: () => {
            this.notificationService
              .info('Record deleted successfully')
              .then(() => this.loadData());
          },
          error: (err) => this.notificationService.error(err),
        });
      },
    });
  }

  remediate(id: number) {
    console.log('remediate', id);
  }
}
